/**
 * PatientPractitionerSearch — Iter 75. Browse clinicians by discipline and
 * see who's next-available.
 *
 * Mounted at /patient/practitioners.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Search, User, ChevronRight, Calendar } from 'lucide-react';

const DISCIPLINES = [
  ['', 'All disciplines'],
  ['physiotherapy', 'Physiotherapy'],
  ['osteopathy', 'Osteopathy'],
  ['chiropractic', 'Chiropractic'],
  ['massage_therapy', 'Massage therapy'],
];

export default function PatientPractitionerSearch() {
  const [discipline, setDiscipline] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/practitioners/search${discipline ? `?discipline=${discipline}` : ''}`);
      setRows(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [discipline]);

  return (
    <div className="p-4 md:p-8 max-w-3xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">FIND A PRACTITIONER</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight" data-testid="prac-title">
          Browse practitioners.
        </h1>
        <p className="text-fg-muted text-sm mt-1">Filter by discipline and see who can fit you in soonest.</p>
      </div>

      <div className="bento-card mb-4">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-fg-muted shrink-0"/>
          <select className="input flex-1" value={discipline}
                  onChange={(e) => setDiscipline(e.target.value)} data-testid="prac-discipline">
            {DISCIPLINES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
      </div>

      {loading ? <div className="text-sm text-fg-muted text-center py-6">Loading…</div> :
       rows.length === 0 ? <div className="text-sm text-fg-muted text-center py-6" data-testid="prac-empty">No practitioners match this filter.</div> : (
        <div className="space-y-2" data-testid="prac-list">
          {rows.map((p) => (
            <div key={p.user_id} className="flex items-center gap-3 p-4 rounded-lg border border-soft hover:border-brand/40 transition" data-testid={`prac-row-${p.user_id}`}>
              <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center shrink-0">
                {p.avatar_url ? <img src={p.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover"/> : <User className="w-5 h-5 text-brand"/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{p.name}</div>
                <div className="text-[11px] text-fg-muted">
                  {(p.disciplines || []).map(d => d.replace('_', ' ')).join(' · ') || 'Generalist'}
                </div>
                {p.bio && <div className="text-[11px] text-fg-muted line-clamp-1 mt-0.5">{p.bio}</div>}
              </div>
              <div className="text-right shrink-0">
                {p.next_available_at ? (
                  <>
                    <div className="text-[11px] text-fg-muted">Next available</div>
                    <div className="text-sm font-semibold flex items-center gap-1">
                      <Calendar className="w-3 h-3"/>
                      {new Date(p.next_available_at).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </>
                ) : (
                  <div className="text-[11px] text-fg-muted">Fully booked 14d+</div>
                )}
                <Link to="/patient/book" className="btn-primary text-xs inline-flex items-center gap-1 mt-1" data-testid={`prac-book-${p.user_id}`}>
                  Book <ChevronRight className="w-3 h-3"/>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
